import get from 'lodash/get';

import { TRADEWIND } from '../../constant/colors';
import { objectKeysToCamelCase } from '../helpers/object-keys-camel-case';
import Assets from '@wetransfer/transfer/assets';

export const AppConfig = {
  // A set of WeTransfer curated wallpapers available for users when onboarding
  curatedWallpapers: objectKeysToCamelCase(
    get(window, '__curated_wallpapers__', [])
  ),

  // Describe the runtime environment
  env: {
    development: __DEV__ === true,
  },

  // Configuration and feature flags from Amplitude
  amplitude: {
    ...(get(window, '__amplitude__.user') !== undefined && {
      user: get(window, '__amplitude__.user'),
    }),
    getAmplitudeDeviceId() {
      return get(window, '__amplitude__.deviceId');
    },
    getAmplitudeUserAgentInfo() {
      return get(window, '__amplitude__.userAgent');
    },
  },

  consentFlags: get(window, '__consent_flags__') || [],

  // Google Recaptcha configuration
  recaptcha: objectKeysToCamelCase(get(window, '__recaptcha__', {})),

  accountAppUrl: get(
    window,
    'accountAppUrl',
    `https://myaccount.${window.location.hostname}/`
  ),

  portalsUrl: get(
    window,
    'portalsUrl',
    `https://portals.${window.location.hostname}/`
  ),

  // Configuration for Stripe
  stripe: {
    ...objectKeysToCamelCase(get(window, '__stripe__', {})),
    url: 'https://js.stripe.com/v3/',
  },

  // Cookie names used by the application
  cookies: {
    takeover: {
      name: 'wt_takeover',
    },
    plus: {
      name: 'wt_plus',
    },
    signout: {
      name: 'wt_signout',
    },
    featureBanner: {
      name: 'wt_banner',
    },
    transferType: {
      name: 'wt_ttype',
    },
    termsAndConditions: {
      name: 'wt_tandc',
    },
    privacy: {
      name: 'wt_privacy',
    },
    referral: {
      name: 'wt_trk',
    },
    firstVisit: {
      name: 'wt_first_visit',
    },
    snowplow: {
      name: '_wt_snowplow',
    },
    projectsOnboarded: {
      name: 'wt_projects_onboarded',
    },
    portalsOnboarded: {
      name: 'wt_portals_onboarded',
    },
    portalsCalloutSeenOnce: {
      name: 'wt_portals_callout_seen_once',
    },
    portalsCalloutSeen: {
      name: 'wt_portals_callout_seen',
    },
    currentTeam: {
      name: 'current-team',
    },
    seenDisplayNameTooltip: {
      name: 'seen-display-name-tooltip',
      count: 0,
    },
    teamsOnboardingCallout: {
      name: 'teams-onboarding-callout',
    },
    teamsOnboardingCalloutDate: {
      name: 'wt_teams_onboarding_callout_date',
    },
    dismissTeamsDrawerForever: {
      name: 'wt_teams_dismiss_drawer_forever',
    },
    showTeamsTooltip: {
      name: 'wt_show_teams_tooltip',
    },
    teamsDrawerSeenOnce: {
      name: 'wt_teams_drawer_seen_once',
    },
    customWallpaperPopoverSeenCount: {
      name: 'wt_custom_wallpaper_drawer_seen_count',
    },
    customWallpaperPopoverDismissedForever: {
      name: 'wt_custom_wallpaper_drawer_dismissed_forever',
    },
    customWallpaperPopoverClosed: {
      name: 'wt_custom_wallpaper_drawer_closed',
    },
    dismissedTeamInlineCuesSent: {
      name: 'wt_team_dismissed_inline_cues_sent',
    },
    dismissedTeamInlineCuesReceived: {
      name: 'wt_team_dismissed_inline_cues_received',
    },
    overrideLanguage: {
      name: 'wt_lang',
    },
    showPricingPageToFAExperiment: {
      name: 'wt_showPricingPageToFAExperiment',
    },
    discountModalSeen: {
      name: 'wt_discount_modal_seen',
    },
  },

  localStorage: {
    editableSubdomain: {
      name: 'showChangeSubdomainNotification',
    },
    currentOrderAccessToken: {
      name: 'currentOrderAccessToken',
    },
  },

  //The notification banner that we show next to the WeTransfer logo
  featureBanner: {
    featureTranslationKey: null, //eg: 'page.profile.wallpapers.notice'
    featureName: null,
    shouldShowFeatureBanner: false,
  },

  // Should this be part of user rules object?
  profile: {
    minPasswordLength: 6,
  },

  subdomainName: {
    maxLength: 63,
  },

  // Top right navigation config
  navigation: {
    tooltip: {
      showDelay: 2000,
      hideDelay: {
        onboarding_success: 8000,
        already_signed_in: 4000,
      },
    },
  },

  textfield: {
    debounceDelay: 300,
  },

  // Redux actions config
  actions: {
    extended_pending: 10000,
  },

  // Tooltips configuration
  notification: {
    animation_duration: 200,
    duration: 5000,
  },

  // Should this be part of transfer configuration
  transfer: {
    showWarningThreshold: 6 * 24 * 60 * 60 * 1000,
    precisionThreshold: 1024 * 1024 * 1024, // We want to show GB's with 1 decimals, and MB's without decimals
    verificationCodeLength: 6,
  },

  twoFactorAuthentication: {
    codeLength: 6,
    codeMaxLength: 13,
  },

  // Override styles for navigation routes
  route: {
    '/': {
      navStyle: {
        backgroundColor: TRADEWIND,
      },
    },
    '/pro': {
      navStyle: {
        backgroundImage: `url(${Assets.lookup('plus/header.png')})`,
      },
    },
  },

  // Panels config
  panel: {
    full_panel_breakpoint: 1049, // At this point, a half panel will become a full panel
    openDelay: 3000,
  },

  // Plus avatars for users without a profile image
  avatars: [
    Assets.lookup('avatars/01.png'),
    Assets.lookup('avatars/02.png'),
    Assets.lookup('avatars/03.png'),
    Assets.lookup('avatars/04.png'),
    Assets.lookup('avatars/05.png'),
    Assets.lookup('avatars/06.png'),
    Assets.lookup('avatars/07.png'),
    Assets.lookup('avatars/08.png'),
    Assets.lookup('avatars/09.png'),
    Assets.lookup('avatars/10.png'),
    Assets.lookup('avatars/11.png'),
    Assets.lookup('avatars/12.png'),
  ],

  // Mobile apps upsell banners
  mobileAppBanner: {
    active: true,
    downloadUrl: 'https://wetransfer.com/products',
    downloadUrliOS:
      'https://itunes.apple.com/app/apple-store/id765359021?pt=658364&ct=wetransfer-web-popup&mt=8',
    downloadUrlAndroid:
      'https://play.google.com/store/apps/details?id=com.wetransfer.app.live&referrer=utm_source%3Dwetransfer%26utm_medium%3Dweb-popup',
    dynamicLinkBaseUrl: location.host.includes('wetransfer.com')
      ? 'https://collectapp.page.link/'
      : 'https://debugcollectapp.page.link/',
    dynamicLinkParams: {
      st: 'Collect by WeTransfer',
      sd: 'The best way to save, organize and share ideas from all your apps',
      efr: '1',
      ibi: 'com.wetransfer.app',
      isi: 765359021,
      apn: location.host.includes('wetransfer.com')
        ? 'com.wetransfer.app.live'
        : 'com.wetransfer.app.live.debug',
      afl: 'https://play.google.com/store/apps/details?id=com.wetransfer.app.live',
      pt: '658364',
      ct: 'wetransfer-web-popup',
      mt: '8',
      utm_source: 'wetransfer',
      utm_medium: 'web-popup',
    },
  },

  supportedBrowsers: [
    'Chrome >= 42',
    'CriOS >= 42',
    'Firefox >= 38',
    'FxiOS >= 9',
    'Safari >= 6',
    'Explorer >= 11',
    'Edge >= 12',
  ],

  // Limits for assets upload
  assetUpload: {
    wallpaperResolution: '1920 x 1200',
    wallpaperVideoResolution: '1920 x 1080',
    emailBackgroundResolution: '1600 x 800',
  },
  // analytics sdks we use
  analytics: {
    sdks: {
      facebook: true,
    },
  },

  termsAndConditions: {
    consentId: '1',
    consentVersion: '20190527',
  },

  privacy: {
    consentId: '3',
    consentVersion: '20190527',
  },

  payment: {
    chargeableStatus: ['chargeable', 'consumed'],
  },

  // Real time chat for Pro users
  zendesk: {
    baseUrl: 'https://wetransfer.zendesk.com/hc/en-us/',
    article_transfer_duration:
      'articles/202603916-How-long-are-uploads-available-to-download-',
  },

  // Salesforce form
  salesforce: {
    postLeadURL: __DEV__
      ? 'https://test.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8'
      : 'https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8',
    OID: __DEV__ ? '00D6E0000009CcS' : '00D0Y000000YKnV',
    budgetFieldID: '00N0Y00000A2Yi6', // ID for custom field generated in Salesforce
  },

  layout: {
    lists: {
      initialItemsNumber: {
        downloader: 5,
        detail: 10,
        progress: 10,
      },
    },
  },

  // Configuration for ProfitWell Retain snippet
  profitWell: {
    attributes: {
      id: 'profitwell-js',
      'data-pw-auth': get(window, 'profitWellPublicAPIToken', ''),
    },
    snippet: `
      (function(i,s,o,g,r,a,m){i[o]=i[o]||function(){(i[o].q=i[o].q||[]).push(arguments)};
      a=s.createElement(g);m=s.getElementsByTagName(g)[0];a.async=1;a.src=r+'?auth='+
      s.getElementById(o+'-js').getAttribute('data-pw-auth');m.parentNode.insertBefore(a,m);
      })(window,document,'profitwell','script','https://public.profitwell.com/js/profitwell.js');
      profitwell('start', {});`,
  },

  customWallpaperServiceUrl: get(window, 'customWallpaperServiceUrl', ''),
};
